import qs from 'qs';

import { ALL_UUID } from '@/shared/constants';
import { routes } from '@/shared/routes';
import { DueTimeFrame, DueTimeFrameType, RecordsStatus, RecurringTaskStatus, TaskStatus } from '@/shared/types/enum';
import { formatIsoDate } from '@/shared/utils/date';
import { merge } from '@/shared/utils/object';
import { utcSubDays, utcToday } from '@/shared/utils/utc';

import { CompanyView, RecurringTaskView, SmartTaskView, TaskView } from '@prisma/client';

export const COMPANIES_HOUSE_URL_TEMPLATE =
  'https://find-and-update.company-information.service.gov.uk/company/${value}';

export const COMPANY_URL_TEMPLATE = routes.companies.edit({ id: '${value}' });

export const INDIVIDUAL_URL_TEMPLATE = routes.individuals.edit({ id: '${value}' });

export const appendQuery = (url: string, query: Record<string, string | undefined>, encode?: boolean) =>
  url + (url.indexOf('?') === -1 ? '?' : '&') + qs.stringify(query, { encode: encode !== undefined ? encode : true });

export const sortedQueryString = (params: Record<string, any>) => {
  const sortedParams = Object.entries(params).sort(([a], [b]) => a.localeCompare(b));
  return new URLSearchParams(sortedParams).toString();
};

export const offcanvasUrl = (url: string, query?: Record<string, string | undefined>) =>
  appendQuery(url, { offcanvas: 'true', ...query });

export const offcanvasBackUrl = (url: string, query?: Record<string, string | undefined>) =>
  appendQuery(url, { offcanvas: 'true', back: 'true', ...query });

export const companiesHouseUrl = (registrationNumber: string) =>
  COMPANIES_HOUSE_URL_TEMPLATE.replaceAll('${value}', registrationNumber);

const filterUrl = <T>(url: string, query: Partial<Record<keyof T, string>>) =>
  appendQuery(
    url,
    Object.keys(query)
      .map((key) => {
        return { [`f_${key}`]: query[key as keyof T]! };
      })
      .reduce(merge),
    false,
  );

export const companiesFilterUrl = (params: {
  userId?: string;
  dueTimeFrame?: DueTimeFrame;
  dueTimeFrameType?: DueTimeFrameType;
}) => {
  const { userId, dueTimeFrame, dueTimeFrameType } = params;

  const query: Partial<Record<keyof CompanyView, string>> = {};

  if (userId) {
    query.accountManagerId = userId;
  }

  if (dueTimeFrame) {
    let dueTimeFramesField: keyof CompanyView = 'dueTimeFrames';

    if (dueTimeFrameType === DueTimeFrameType.Accounts) {
      dueTimeFramesField = 'accountsDueTimeFrames';
    } else if (dueTimeFrameType === DueTimeFrameType.AmlCheck) {
      dueTimeFramesField = 'amlCheckDueTimeFrames';
    } else if (dueTimeFrameType === DueTimeFrameType.Bookkeeping) {
      dueTimeFramesField = 'bookkeepingDueTimeFrames';
    } else if (dueTimeFrameType === DueTimeFrameType.CisReturn) {
      dueTimeFramesField = 'cisReturnDueTimeFrames';
    } else if (dueTimeFrameType === DueTimeFrameType.ConfirmationStatement) {
      dueTimeFramesField = 'confirmationStatementDueTimeFrames';
    } else if (dueTimeFrameType === DueTimeFrameType.Payroll) {
      dueTimeFramesField = 'payrollDueTimeFrames';
    } else if (dueTimeFrameType === DueTimeFrameType.TaxReturn) {
      dueTimeFramesField = 'taxReturnDueTimeFrames';
    } else if (dueTimeFrameType === DueTimeFrameType.VatReturn) {
      dueTimeFramesField = 'vatReturnDueTimeFrames';
    }

    query[dueTimeFramesField] = `contains|${dueTimeFrame}`;
  }

  return filterUrl(routes.companies.list({ archived: false }), query);
};

export const tasksFilterUrl = (params: {
  clientId?: string;
  serviceId?: string;
  userId?: string;
  dueTimeFrame?: DueTimeFrame;
  recordsStatus?: RecordsStatus;
  status?: TaskStatus;
  preset?: 'uncompleted' | 'completed30Days';
}) => {
  const { clientId, serviceId, userId, dueTimeFrame, recordsStatus, status, preset } = params;

  const query: Partial<Record<keyof TaskView, string>> = {};

  if (clientId) {
    query.clientId = clientId;
  }

  if (serviceId) {
    query.serviceId = serviceId !== ALL_UUID ? serviceId : 'isnull';
  }

  if (userId) {
    query.assignedToUserId = userId;
  }

  if (dueTimeFrame) {
    query.dueTimeFrames = `contains|${dueTimeFrame}`;
  }

  if (recordsStatus) {
    query.recordsStatus = recordsStatus;
  }

  if (status) {
    query.status = status;
  }

  if (preset === 'uncompleted') {
    query.status = `notequal|${TaskStatus.Completed}`;
  } else if (preset === 'completed30Days') {
    query.completedDate = `greaterthanorequal|${formatIsoDate(utcSubDays(utcToday(), 30))}`;
    query.status = TaskStatus.Completed;
  }

  return filterUrl(routes.tasks.list(), query);
};

export const recurringTasksFilterUrl = (params: {
  clientId?: string;
  serviceId?: string;
  userId?: string;
  status?: RecurringTaskStatus;
}) => {
  const { clientId, serviceId, userId, status } = params;

  const query: Partial<Record<keyof RecurringTaskView, string>> = {};

  if (clientId) {
    query.clientId = clientId;
  }

  if (serviceId) {
    query.serviceId = serviceId;
  }

  if (userId) {
    query.assignedToUserId = userId;
  }

  if (status) {
    query.status = status;
  }

  return filterUrl(routes.recurringTasks.list(), query);
};

export const smartTasksFilterUrl = (params: { serviceId?: string; userId?: string }) => {
  const { serviceId, userId } = params;

  const query: Partial<Record<keyof SmartTaskView, string>> = {};

  if (serviceId) {
    query.serviceId = serviceId;
  }

  if (userId) {
    query.assignedToUserId = userId;
  }

  return filterUrl(routes.smartTasks.list(), query);
};
